import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';

import { carriesFiles, droppedFiles } from '@/entities/photo';

import { takePhotoFiles } from '../components/photo-intake';

export interface PhotoDropHandlers {
    onDragEnter: (event: DragEvent<HTMLElement>) => void;
    onDragOver: (event: DragEvent<HTMLElement>) => void;
    onDragLeave: (event: DragEvent<HTMLElement>) => void;
    onDrop: (event: DragEvent<HTMLElement>) => void;
}

export interface PhotoDrop {
    /** Er hangen bestanden boven dit vlak. */
    active: boolean;
    handlers: PhotoDropHandlers;
}

/**
 * Foto's slepen: uit de Foto's-app op een iPad (Split View of Slide Over) of
 * uit de Finder op een Mac, één of meer tegelijk.
 *
 * - Tijdens het slepen zijn de bestanden nog niet leesbaar; `types` zegt wel
 *   dat er bestanden in zitten. Alleen dan wordt het vlak een dropzone.
 * - `dragenter` en `dragleave` vuren ook bij elk kind-element; een teller
 *   voorkomt dat de dropzone flikkert. `dragleave` verlaagt die teller altijd:
 *   Safari levert bij het verlaten niet overal nog `types`, en een dropzone die
 *   dan aan blijft staan, blijft aan tot je de vraag verlaat.
 * - `dragover` moet `preventDefault` krijgen, anders is neerzetten niet
 *   toegestaan en opent Safari de foto in plaats van hem toe te voegen.
 * - `stopPropagation`: een drop op de fotostrip telt niet nog eens voor de
 *   kaart eromheen.
 * - Wordt het slepen afgebroken (Escape, of losgelaten buiten de kaart), dan
 *   komt er geen `dragleave` meer; `dragend` en een drop elders zetten de
 *   dropzone alsnog uit.
 */
export function usePhotoDrop(onFiles: (files: File[]) => void): PhotoDrop {
    const [active, setActive] = useState(false);
    const depth = useRef(0);

    const onDragEnter = useCallback((event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event.dataTransfer)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        depth.current += 1;
        setActive(true);
    }, []);

    const onDragOver = useCallback((event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event.dataTransfer)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }
    }, []);

    const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
        if (depth.current === 0) {
            return;
        }

        event.stopPropagation();
        depth.current -= 1;

        if (depth.current === 0) {
            setActive(false);
        }
    }, []);

    const onDrop = useCallback(
        (event: DragEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            depth.current = 0;
            setActive(false);

            void takePhotoFiles(droppedFiles(event.dataTransfer), onFiles);
        },
        [onFiles],
    );

    useEffect(() => {
        if (!active || typeof window === 'undefined') {
            return;
        }

        const reset = () => {
            depth.current = 0;
            setActive(false);
        };

        window.addEventListener('dragend', reset);
        window.addEventListener('drop', reset);

        return () => {
            window.removeEventListener('dragend', reset);
            window.removeEventListener('drop', reset);
        };
    }, [active]);

    return { active, handlers: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}

/**
 * Valt een foto nét naast een vraag, dan opent de browser hem standaard - en
 * is de inspecteur zijn opname kwijt. Zolang het opnamescherm open is, doet
 * een drop buiten een dropzone daarom niets.
 */
export function useIgnoreStrayFileDrops(): void {
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const ignore = (event: globalThis.DragEvent) => {
            if (carriesFiles(event.dataTransfer)) {
                event.preventDefault();
            }
        };

        window.addEventListener('dragover', ignore);
        window.addEventListener('drop', ignore);

        return () => {
            window.removeEventListener('dragover', ignore);
            window.removeEventListener('drop', ignore);
        };
    }, []);
}
